import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getYears } from '../../services/core/coreService';
import { UserContext } from './UserContext';

export const ExamYearContext = createContext(null);

// Optimistic value so the very first paint has something to render. It is
// replaced as soon as /core/exam_years/ answers. Its uuid matches nothing in the
// database, so it must never be what the app ends up working with — that is the
// state the app used to get stuck in when the year request failed.
const FALLBACK_YEAR = {
  code: null,
  current: true,
  end_date: '2026-12-31',
  name: 'Year - 2026',
  start_date: '2026-01-01',
  uuid: '85d30fde-f7cb-4ab9-9955-675d1ba0f76c',
  year: 2026,
};

const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

export const ExamYearProvider = ({ children }) => {
  const { user } = useContext(UserContext);

  const [examYear, setExamYear] = useState(FALLBACK_YEAR);
  const [yearList, setYearList] = useState([FALLBACK_YEAR]);
  const [currentYear, setCurrentYear] = useState(FALLBACK_YEAR);
  const [yearsLoaded, setYearsLoaded] = useState(false);

  const loadedRef = useRef(false);
  // Once the user picks a year by hand, a late-arriving response must not
  // silently move them back to the current one.
  const examYearTouchedRef = useRef(false);

  // Login navigates client-side, so this provider is never remounted. Keying the
  // effect on the user means a fetch that failed while logged out is retried
  // once the session exists, instead of leaving the fallback in place forever.
  useEffect(() => {
    if (loadedRef.current) {
      return;
    }

    let cancelled = false;
    let retryTimer = null;

    const load = (attempt = 1) => {
      getYears()
        .then((res) => {
          if (cancelled) {
            return;
          }

          const results = Array.isArray(res?.results) ? res.results : [];

          if (!results.length) {
            throw new Error('Exam year list is empty');
          }

          loadedRef.current = true;
          setYearList(results);
          setYearsLoaded(true);

          // `current` is computed server-side from today's date, so no year is
          // marked current if today falls outside every configured range. Fall
          // back to the newest year (the queryset is ordered by year ascending)
          // rather than storing undefined.
          const resolvedCurrent =
            results.find((y) => y?.current === true) ||
            results[results.length - 1];

          setCurrentYear(resolvedCurrent);

          if (!examYearTouchedRef.current) {
            setExamYear(resolvedCurrent);
          }
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }

          if (attempt < MAX_ATTEMPTS) {
            retryTimer = setTimeout(
              () => load(attempt + 1),
              RETRY_BASE_DELAY_MS * attempt,
            );
            return;
          }

          console.error('Failed to load exam years', err);
        });
    };

    load();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [user?.uuid]);

  const updateExamYear = useCallback((newYear) => {
    if (!newYear) {
      return;
    }

    examYearTouchedRef.current = true;
    setExamYear(newYear);
  }, []);

  return (
    <ExamYearContext.Provider
      value={{ yearList, examYear, updateExamYear, currentYear, yearsLoaded }}
    >
      {children}
    </ExamYearContext.Provider>
  );
};
